import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

interface Story {
  id: string;
  slug: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  excerpt_en: string;
  excerpt_ps: string;
  excerpt_da: string;
  author_en: string;
  author_ps: string;
  author_da: string;
  author_role_en: string;
  author_role_ps: string;
  author_role_da: string;
  image: string;
  category: string;
  read_time_en: string;
  read_time_ps: string;
  read_time_da: string;
  created_at: string;
}

const categoryKeyMap: Record<string, string> = {
  All: "storiesPage.filters.all",
  "Field Stories": "storiesPage.filters.fieldStories",
  "Impact Reports": "storiesPage.filters.impactReports",
  "Volunteer Voices": "storiesPage.filters.volunteerVoices",
  "News & Updates": "storiesPage.filters.news",
};

const categoryColorMap: Record<string, string> = {
  "Field Stories": "bg-emerald-100 text-emerald-700",
  "Impact Reports": "bg-amber-100 text-amber-700",
  "Volunteer Voices": "bg-rose-100 text-rose-700",
  "News & Updates": "bg-sky-100 text-sky-700",
};

const storyCategories = [
  "All",
  "Field Stories",
  "Impact Reports",
  "Volunteer Voices",
  "News & Updates",
];

function getLocalizedField(story: Story, field: string, lang: string): string {
  const suffix = lang === "ps" ? "_ps" : lang === "da" ? "_da" : "_en";
  return (story as any)[field + suffix] || (story as any)[field + "_en"] || "";
}

export default function StoriesPage() {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("All");
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("stories")
      .select("*")
      .eq("published", true)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        setStories((data as Story[]) ?? []);
        setLoading(false);
      });
  }, []);

  const filtered =
    activeCategory === "All"
      ? stories
      : stories.filter((s) => s.category === activeCategory);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(
      i18n.language === "ps"
        ? "ps-AF"
        : i18n.language === "da"
        ? "fa-AF"
        : "en-US",
      { year: "numeric", month: "long", day: "numeric" }
    );
  };

  return (
    <main className="pt-24">
      {/* Header */}
      <section className="py-14 md:py-20 bg-[#1B2838]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">
            {t("storiesPage.tag")}
          </span>
          <h1
            className="text-3xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("storiesPage.title")}
          </h1>
          <p className="text-gray-400 text-base max-w-2xl mx-auto leading-relaxed">
            {t("storiesPage.subtitle")}
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="bg-white border-b border-gray-100 sticky top-[100px] z-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex flex-wrap items-center gap-2">
          {storyCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                activeCategory === cat
                  ? "bg-[#1B2838] text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {t(categoryKeyMap[cat])}
            </button>
          ))}
        </div>
      </section>

      {/* Stories Grid */}
      <section className="py-14 md:py-20 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-8 h-8 border-2 border-[#4ECDC4] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-gray-400 text-sm mt-3">Loading stories...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <i className="ri-book-open-line text-5xl mb-4 block"></i>
              <p className="text-lg">{t("storiesPage.empty")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filtered.map((story) => (
                <Link
                  key={story.id}
                  to={`/stories/${story.slug}`}
                  className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#4ECDC4]/40 transition-all duration-300 group flex flex-col cursor-pointer"
                >
                  <div className="relative h-48 sm:h-52 md:h-56 overflow-hidden flex-shrink-0">
                    <img
                      src={story.image}
                      alt={getLocalizedField(story, "title", i18n.language)}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          categoryColorMap[story.category] || "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {t(categoryKeyMap[story.category])}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {formatDate(story.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {getLocalizedField(story, "read_time", i18n.language)}
                      </span>
                    </div>
                    <h3 className="font-bold text-[#1B2838] text-lg mb-2 leading-tight group-hover:text-[#4ECDC4] transition-colors">
                      {getLocalizedField(story, "title", i18n.language)}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1 line-clamp-3">
                      {getLocalizedField(story, "excerpt", i18n.language)}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-400">
                        {t("storiesPage.by")}{" "}
                        <strong className="text-[#1B2838]">
                          {getLocalizedField(story, "author", i18n.language)}
                        </strong>
                      </span>
                      <span className="text-[#4ECDC4] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        {t("storiesPage.readMore")}
                        <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}